import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Spinner } from 'react-bootstrap';
import {
  strapiApiUrl,
  FILE_UPLOAD_MAX_BYTES,
} from '../../../../utils/constants';
import {
  sanitizeFilename,
  createSanitizedFile,
} from '../../../../utils/fileUtils';
import { Context } from '../Wizard/WizardWrapper';
import { axiosDelete } from '../../../../utils/axiosDelete';
import { axiosPost } from '../../../../utils/axiosPost';

import DownloadFile from './DownloadFile';
import ModalConfirmation from '../../ModalConfirmation';
import { devError } from '../../../../utils/devLog';

const InputFile = ({
  name,
  fileExtension,
  disabled = false,
  fileUploading,
  setFileUploading,
}) => {
  const [showModal, setShowModal] = useState(false);
  const { register, watch, setValue, resetField, setError, trigger } =
    useContext(Context);
  const [uploadingField, setUploadingField] = useState(null);

  const htmlFileExtensions = fileExtension
    .map((item) => `.${item.attributes.name}`)
    .join(' ');

  const handleCancelSelect = () => {
    setShowModal(false);
  };

  const deleteExistingFile = async (fieldName) => {
    const existingFileId = watch(`${fieldName}FileId`).id;
    const deletedFile = await axiosDelete(`upload/files/${existingFileId}`);

    if (deletedFile?.status === 200 || deletedFile === undefined) {
      setValue(`${fieldName}`, null);
      setValue(`${fieldName}FileId`, null);
      setValue(`${fieldName}FileName`, '');
      setValue(`${fieldName}FilePath`, '');
      setShowModal(false);
      trigger();
    }
  };

  const clearFileFields = (fieldName) => {
    setValue(`${fieldName}`, null);
    setValue(`${fieldName}FileId`, null);
    setValue(`${fieldName}FileName`, '');
    setValue(`${fieldName}FilePath`, '');
  };

  const inputFileHandler = async (event) => {
    const file = event?.target?.files?.[0];

    if (!file) return;

    const currentFileExtension = file.type;
    const fieldExtensionOptions = fileExtension.map(
      (item) => item.attributes.name,
    );

    const extensionExists = fieldExtensionOptions.some((option) =>
      currentFileExtension.includes(option),
    );

    if (!extensionExists) {
      trigger(event.target.name);
      return;
    }

    const isValidSize = file.size > 0 && file.size <= FILE_UPLOAD_MAX_BYTES;

    if (!isValidSize) {
      trigger(event.target.name);
      return;
    }

    const sanitizedFilename = sanitizeFilename(file.name);
    const sanitizedFile = createSanitizedFile(file);

    const formData = new FormData();
    formData.append('files', sanitizedFile);

    const existingFileId = watch(`${name}FileId`);

    if (
      existingFileId &&
      typeof existingFileId === 'object' &&
      Object.values(existingFileId).length !== 0
    ) {
      await deleteExistingFile(name);
    }
    setFileUploading(true);
    setUploadingField(name);
    axiosPost('upload', formData)
      .then((response) => {
        if (response?.status === 200 && response.data?.[0]?.id) {
          setValue(`${event.target.name}FileName`, sanitizedFilename);
          setValue(`${event.target.name}FileId`, { id: response.data[0].id });
          setValue(
            `${event.target.name}FilePath`,
            `${strapiApiUrl.replace('/api/', '')}${response.data[0].url}`,
          );
          trigger();
        } else {
          devError(`File ${event.target.name} was not uploaded`);
          clearFileFields(event.target.name);
        }
      })
      .catch((error) => {
        devError('Upload error:', error);
        clearFileFields(event.target.name);
      })
      .finally(() => {
        setFileUploading(false);
        setUploadingField(null);
      });
  };

  useEffect(() => {
    const currentFile = watch(name);
    const currentFileId = watch(`${name}FileId`);

    if (
      currentFile &&
      typeof currentFile === 'object' &&
      currentFile.length === 0 &&
      currentFileId
    ) {
      resetField(`${name}FileId`);
      resetField(`${name}FileName`);
      resetField(`${name}FilePath`);
    }
  }, [watch(name), watch(`${name}FileId`), name, resetField]);

  return (
    <>
      <input
        name={`${name}FileId`}
        type="hidden"
        {...register(`${name}FileId`)}
      />
      <input
        name={`${name}FileName`}
        type="hidden"
        {...register(`${name}FileName`)}
      />
      <input
        name={`${name}FilePath`}
        type="hidden"
        {...register(`${name}FilePath`)}
      />

      <input
        name={name}
        type="file"
        className={`form-control mb-3 ${
          watch(`${name}FileName`) && watch(`${name}FileName`).trim().length > 0
            ? 'successful-upload'
            : ''
        }`}
        placeholder="upload"
        accept={htmlFileExtensions || ''}
        {...register(name, {
          onChange: (event) => inputFileHandler(event),
        })}
        disabled={disabled}
      />
      {name === uploadingField && (
        <div className="mt-3">
          <Spinner animation="border" role="status" />
          <span className="mx-3">Uploading...</span>
        </div>
      )}

      <ModalConfirmation
        showModal={showModal}
        handleCancelSelect={handleCancelSelect}
        bodyText="Are you sure you want to **delete** this file? **this action cannot be undone**"
        handleConfirmSelect={deleteExistingFile}
        rowId={name}
      />

      {watch(`${name}FileName`) &&
        watch(`${name}FilePath`) &&
        watch(`${name}FileId`) && (
          <div>
            <DownloadFile
              text={watch(`${name}FileName`)}
              url={watch(`${name}FilePath`)}
              name={name}
            />
            <button
              type="button"
              className="btn btn-outline-danger fns-delete-icon"
              onClick={() => {
                setShowModal(true);

                if (!watch(`${name}`)) {
                  setError(name, {
                    message: 'Required Field',
                    type: 'required',
                    ref: { name },
                  });
                }
              }}
            >
              <i className="bi bi-trash" />
              <span className="visually-hidden">Button</span>
            </button>
          </div>
        )}
    </>
  );
};

InputFile.propTypes = {
  name: PropTypes.string,
  fileExtension: PropTypes.array,
  disabled: PropTypes.bool,
  fileUploading: PropTypes.bool,
  setFileUploading: PropTypes.func,
};

export default InputFile;
